import React, { useEffect } from 'react';
import { Typography, Card, Form, Button, message, Space, Layout, InputNumber, Select } from 'antd';
import { SaveOutlined, CalendarOutlined } from '@ant-design/icons';
import { DEFAULT_DEADLINE_SETTINGS, getDeadlineSettings, saveDeadlineSettings } from '../../utils/deadlineSettings';

const { Title, Text } = Typography;
const { Content } = Layout;

const monthOptions = [
  { value: 1, label: 'Jan' },
  { value: 2, label: 'Feb' },
  { value: 3, label: 'Mar' },
  { value: 4, label: 'Apr' },
  { value: 5, label: 'May' },
  { value: 6, label: 'Jun' },
  { value: 7, label: 'Jul' },
  { value: 8, label: 'Aug' },
  { value: 9, label: 'Sep' },
  { value: 10, label: 'Oct' },
  { value: 11, label: 'Nov' },
  { value: 12, label: 'Dec' },
];

export const DeadlineSettingsPage: React.FC = () => {
  const [form] = Form.useForm();

  useEffect(() => {
    const settings = getDeadlineSettings();
    form.setFieldsValue({
      personalBMonth: settings.personal.b.month,
      personalBDay: settings.personal.b.day,
      personalBEMonth: settings.personal.be.month,
      personalBEDay: settings.personal.be.day,
      accountOffsetMonths: settings.business.accountOffsetMonths,
      auditOffsetMonths: settings.business.auditOffsetMonths,
      taxOffsetMonths: settings.business.taxOffsetMonths,
      annualReturnOffsetYears: settings.business.annualReturnOffsetYears,
    });
  }, [form]);

  const handleSave = async (values: any) => {
    try {
      saveDeadlineSettings({
        personal: {
          b: {
            month: values.personalBMonth,
            day: values.personalBDay,
          },
          be: {
            month: values.personalBEMonth,
            day: values.personalBEDay,
          },
        },
        business: {
          accountOffsetMonths: values.accountOffsetMonths,
          auditOffsetMonths: values.auditOffsetMonths,
          taxOffsetMonths: values.taxOffsetMonths,
          annualReturnOffsetYears: values.annualReturnOffsetYears,
        },
      });
      message.success('Deadline settings saved successfully');
    } catch {
      message.error('Failed to save deadline settings');
    }
  };

  const handleReset = () => {
    form.setFieldsValue({
      personalBMonth: DEFAULT_DEADLINE_SETTINGS.personal.b.month,
      personalBDay: DEFAULT_DEADLINE_SETTINGS.personal.b.day,
      personalBEMonth: DEFAULT_DEADLINE_SETTINGS.personal.be.month,
      personalBEDay: DEFAULT_DEADLINE_SETTINGS.personal.be.day,
      accountOffsetMonths: DEFAULT_DEADLINE_SETTINGS.business.accountOffsetMonths,
      auditOffsetMonths: DEFAULT_DEADLINE_SETTINGS.business.auditOffsetMonths,
      taxOffsetMonths: DEFAULT_DEADLINE_SETTINGS.business.taxOffsetMonths,
      annualReturnOffsetYears: DEFAULT_DEADLINE_SETTINGS.business.annualReturnOffsetYears,
    });
  };

  return (
    <Content style={{ padding: '24px' }}>
      <Title level={2}>
        <CalendarOutlined style={{ marginRight: '8px' }} />
        Deadline Settings
      </Title>
      <Text type="secondary">Configure recurring deadline rules for calendar deadlines</Text>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSave}
        style={{ marginTop: '24px' }}
      >
        <Card title="Personal (Customer) Assessment" style={{ marginBottom: '24px' }}>
          <Space size="large" wrap>
            <Form.Item
              label="B Month"
              name="personalBMonth"
              rules={[{ required: true, message: 'Select month' }]}
            >
              <Select options={monthOptions} style={{ width: 120 }} />
            </Form.Item>
            <Form.Item
              label="B Day"
              name="personalBDay"
              rules={[{ required: true, message: 'Enter day' }]}
            >
              <InputNumber min={1} max={31} style={{ width: 120 }} />
            </Form.Item>
            <Form.Item
              label="BE Month"
              name="personalBEMonth"
              rules={[{ required: true, message: 'Select month' }]}
            >
              <Select options={monthOptions} style={{ width: 120 }} />
            </Form.Item>
            <Form.Item
              label="BE Day"
              name="personalBEDay"
              rules={[{ required: true, message: 'Enter day' }]}
            >
              <InputNumber min={1} max={31} style={{ width: 120 }} />
            </Form.Item>
          </Space>
        </Card>

        <Card title="Business Deadlines" style={{ marginBottom: '24px' }}>
          <Space size="large" wrap>
            <Form.Item
              label="Account (Year End + months)"
              name="accountOffsetMonths"
              rules={[{ required: true, message: 'Enter months' }]}
            >
              <InputNumber min={0} max={24} style={{ width: 180 }} />
            </Form.Item>
            <Form.Item
              label="Audit (Year End + months)"
              name="auditOffsetMonths"
              rules={[{ required: true, message: 'Enter months' }]}
            >
              <InputNumber min={0} max={24} style={{ width: 180 }} />
            </Form.Item>
            <Form.Item
              label="Tax (Year End + months)"
              name="taxOffsetMonths"
              rules={[{ required: true, message: 'Enter months' }]}
            >
              <InputNumber min={0} max={24} style={{ width: 180 }} />
            </Form.Item>
            <Form.Item
              label="Annual Return (Incorporation + years)"
              name="annualReturnOffsetYears"
              rules={[{ required: true, message: 'Enter years' }]}
            >
              <InputNumber min={0} max={5} style={{ width: 200 }} />
            </Form.Item>
          </Space>
        </Card>

        <Space>
          <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
            Save Settings
          </Button>
          <Button onClick={handleReset}>
            Reset to Default
          </Button>
        </Space>
      </Form>
    </Content>
  );
};
